import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { SettingsIcon } from "@chakra-ui/icons";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { IoInformationCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { fetchController } from "../../../utils/FetchController/fetchController";
import {
  agencyBillingPlanGroupENUM,
  getHostDomain,
} from "../../../utils/utils";
import AddCustomBillingPlan from "../../Modals/AddCustomBillingPlan";
import AddCustomBillingPlanWithWalletFields from "../../Modals/ConfigureWallet";
import ConfigureMultipliersModal from "../../Modals/MultiplierModal";
import { AgencyContext } from "../../Settings/AgencyProvider/AgencyProvider";
import AgencyBillingPlanTable from "../AgencyBillingPlanTable";

// Shared section wrapper for the numbered-step cards.
function StepCard({ step, title, description, badge, children }) {
  return (
    <Card variant="outline">
      <CardBody>
        <HStack mb={2} spacing={3} align="flex-start">
          <Box
            minW="28px"
            h="28px"
            borderRadius="full"
            bg="blue.50"
            color="blue.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="sm"
            fontWeight="bold"
          >
            {step}
          </Box>
          <Box flex={1}>
            <HStack>
              <Heading size="sm" fontWeight="semibold">
                {title}
              </Heading>
              {badge}
            </HStack>
            {description && (
              <Text fontSize="sm" color="gray.500" mt={1}>
                {description}
              </Text>
            )}
          </Box>
        </HStack>
        <Box pl={{ base: 0, md: "40px" }}>{children}</Box>
      </CardBody>
    </Card>
  );
}

const METHOD_OPTIONS = [
  { label: "Stripe", value: "stripe" },
  { label: "Custom", value: "custom" },
];

export default function RebillingPlan() {
  const baseUrl = getHostDomain();
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme, agencyData, getAgencies } = agency;
  const agency_id = agencyData?.id;
  const [agencyBillingMethod, setAgencyBillingMethod] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: walletOpen,
    onOpen: walletOnOpen,
    onClose: walletOnClose,
  } = useDisclosure();
  const {
    isOpen: multipliersOpen,
    onOpen: multipliersOnOpen,
    onClose: multipliersOnClose,
  } = useDisclosure();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm();

  const [tableData, setTableData] = useState([]);
  const [heads, setHeads] = useState([]);
  const [loading, setIsLoading] = useState(false);
  const [stripeKeyStatus, setStripeKeyStatus] = useState("valid");
  const [webhokkBtnErrorMsg, setWebhokkBtnErrorMsg] = useState("");

  // ─── Fetching ─────────────────────────────────────
  const fetchAgencyBillingPlanList = async () => {
    try {
      setIsLoading(true);
      const url =
        baseUrl + `/api/v1/agency/billing/${agency_id}/billing_plan/list`;
      const response = await fetchController(url, "GET");
      const arrangedTableData = response.data?.items.map((item) => ({
        ...item,
        "Billing Plan Name": item.name,
        "Stripe Lookup Key": item.plan_lookup_key,
        Assistant: item.premium_support ? "Yes" : "No",
        Queries: item.queries_count,
        Seconds: item.voice_seconds,
        Users: item.user_count,
        id: item.id,
        billing_plan_group: item.billing_plan_group,
      }));
      const columns = [
        "Billing Plan Name",
        "Stripe Lookup Key",
        "Assistant",
        "Queries",
        "Seconds",
        "Users",
      ].map((key) => ({
        Header: key,
        accessor: key,
        Filter: ({ column }) => <input {...column.filterProps} />,
      }));
      setTableData(arrangedTableData);
      setHeads(columns);
    } catch (error) {
      console.log("Some went wrong while fetching billing plan list", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (agency_id) fetchAgencyBillingPlanList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency_id]);

  // ─── Save handlers ────────────────────────────────
  const saveBillingPlanPatch = async (patch) => {
    try {
      setIsLoading(true);
      const body = {
        billing_plan: {
          ...agencyData?.billing_plan,
          ...patch,
        },
      };
      await fetchController(
        baseUrl + `/api/v1/agency/${agencyData?.id}`,
        "PUT",
        body
      );
      toast.success("Updated");
      getAgencies();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Oops! Something went wrong!"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBillingMethod = async (data) => {
    await saveBillingPlanPatch({ billing_method: data.billing_method });
    setAgencyBillingMethod(data.billing_method);
  };

  const handleSaveStripeKey = (data) => {
    saveBillingPlanPatch({ stripe_api_key: data.stripeKey });
  };

  const handleClickOnsetWebHook = async () => {
    try {
      await fetchController(
        `${baseUrl}/api/v1/agency/billing/${agencyData?.id}/setup_stripe_webhook`,
        "GET"
      );
      checkStripeDetails();
      toast.success("Webhook set up successfully.");
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const checkStripeDetails = async () => {
    try {
      setStripeKeyStatus("loading");
      await fetchController(
        baseUrl +
          `/api/v1/agency/billing/${agencyData?.id}/check_stripe_webhook`,
        "GET"
      );
      setStripeKeyStatus("valid");
    } catch (error) {
      setStripeKeyStatus("invalid");
      setWebhokkBtnErrorMsg(error.message);
    }
  };

  // ─── Effects (form sync + initial stripe check) ───
  useEffect(() => {
    setAgencyBillingMethod(agencyData?.billing_plan?.billing_method || "");
    setValue("billing_method", agencyData?.billing_plan?.billing_method || "");
    setValue("stripeKey", agencyData?.billing_plan?.stripe_api_key || "");
    setValue(
      "deductAgencyWallet",
      Boolean(agencyData?.billing_plan?.deduct_agency_wallet)
    );
    setValue(
      "subAccountWallet",
      Boolean(agencyData?.billing_plan?.subaccount_wallet)
    );
  }, [agencyData, setValue]);

  useEffect(() => {
    if (
      agencyData?.id &&
      agencyData?.billing_plan?.billing_method !== "custom"
    ) {
      checkStripeDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyData]);

  // ─── Derived ──────────────────────────────────────
  const deductAgencyWallet = watch("deductAgencyWallet");
  const subAccountWallet = watch("subAccountWallet");
  const isStripe = agencyBillingMethod === "stripe";
  const isCustom = agencyBillingMethod === "custom";
  const stripeKeyConfigured = Boolean(agencyData?.billing_plan?.stripe_api_key);
  const webhookBadge =
    stripeKeyStatus === "valid" ? (
      <Badge colorScheme="green">Connected</Badge>
    ) : stripeKeyStatus === "loading" ? (
      <Badge colorScheme="gray">
        <Spinner size="xs" mr={1} /> Checking
      </Badge>
    ) : (
      <Badge colorScheme="red">Not configured</Badge>
    );

  const tabs = Object.keys(agencyBillingPlanGroupENUM).map((key) => ({
    key,
    value: agencyBillingPlanGroupENUM[key],
    children: (
      <AgencyBillingPlanTable
        tab={key}
        activeTab={activeTabIndex}
        tableData={tableData.filter((val) => val.billing_plan_group === key)}
        columns={heads}
        fetch={fetchAgencyBillingPlanList}
        loading={loading}
        tabName={agencyBillingPlanGroupENUM[key]}
        tableName={`agencyBillingPlan_${key}`}
      />
    ),
  }));

  return (
    <>
      <Flex direction="column" gap={4} w="100%" pb={6}>
        {/* Page header */}
        <Box>
          <Heading color={textColor} fontSize="xl">
            Rebilling Plan
          </Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            How you bill your subaccounts and what plans they can pick.
          </Text>
        </Box>

        {/* ─── Step 1: Billing method ─── */}
        <StepCard
          step="1"
          title="Billing method"
          description="Stripe charges subaccounts directly through your Stripe account. Custom lets you invoice them outside the platform."
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(handleSaveBillingMethod)();
            }}
          >
            <Flex gap={3} align="flex-end" maxW="520px">
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Method</FormLabel>
                <Select
                  options={METHOD_OPTIONS}
                  value={METHOD_OPTIONS.find(
                    (o) => o.value === watch("billing_method")
                  )}
                  onChange={({ value }) => setValue("billing_method", value)}
                  isDisabled={isCustom}
                />
              </FormControl>
              <Button
                type="button"
                colorScheme={buttonColorScheme}
                onClick={() => handleSubmit(handleSaveBillingMethod)()}
                isDisabled={isCustom}
              >
                Save
              </Button>
            </Flex>
            {isCustom && (
              <Text fontSize="xs" color="gray.500" mt={2}>
                Custom is irreversible — switching back to Stripe is not
                supported.
              </Text>
            )}
          </form>
        </StepCard>

        {/* ─── Step 2: Stripe credentials (only when Stripe) ─── */}
        {isStripe && (
          <StepCard
            step="2"
            title="Stripe credentials"
            description="Required to charge subaccounts. We use these to create and listen for Stripe events on your behalf."
            badge={webhookBadge}
          >
            <Stack spacing={3} maxW="520px">
              <FormControl isInvalid={errors.stripeKey}>
                <FormLabel fontSize="sm">Stripe API key</FormLabel>
                <Input
                  type={
                    agencyData?.billing_plan?.stripe_api_key
                      ? "password"
                      : "text"
                  }
                  placeholder="sk_live_..."
                  {...register("stripeKey", { required: true })}
                />
                {errors.stripeKey && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    Stripe key is required
                  </Text>
                )}
              </FormControl>
              <Flex gap={2}>
                <Button
                  type="button"
                  colorScheme={buttonColorScheme}
                  onClick={() => handleSubmit(handleSaveStripeKey)()}
                >
                  Save key
                </Button>
                <Tooltip label={webhokkBtnErrorMsg || ""}>
                  <Button
                    variant="outline"
                    isDisabled={!stripeKeyConfigured}
                    colorScheme={
                      stripeKeyStatus === "valid" ? "green" : buttonColorScheme
                    }
                    onClick={handleClickOnsetWebHook}
                  >
                    {stripeKeyStatus === "valid"
                      ? "Re-sync webhook"
                      : "Set webhook"}
                  </Button>
                </Tooltip>
              </Flex>
            </Stack>
          </StepCard>
        )}

        {/* ─── Step 3: Subaccount billing ─── */}
        <StepCard
          step={isStripe ? "3" : "2"}
          title="Subaccount billing"
          description="Choose how your subaccounts pay for AI usage."
        >
          <Stack spacing={4}>
            {/* Subaccount wallet toggle */}
            <Box>
              <HStack mb={1}>
                <Checkbox
                  {...register("subAccountWallet")}
                  isChecked={
                    agencyData?.billing_plan?.subaccount_wallet
                      ? Boolean(agencyData?.billing_plan?.subaccount_wallet)
                      : subAccountWallet
                  }
                  isDisabled={
                    !!agencyData?.billing_plan?.subaccount_wallet || !isStripe
                  }
                  onChange={(e) => {
                    const wasChecked = getValues("subAccountWallet");
                    const nowChecked = e.target.checked;
                    if (!wasChecked && nowChecked) {
                      setValue("subAccountWallet", true, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      saveBillingPlanPatch({
                        subaccount_wallet: true,
                        deduct_agency_wallet: true,
                      });
                    }
                  }}
                >
                  <Text fontWeight="medium">Enable Wallet for subaccounts</Text>
                </Checkbox>
              </HStack>
              <Text fontSize="sm" color="gray.500" pl={6}>
                Subaccounts pay-as-they-go from a prepaid wallet using your
                Stripe. Cost multipliers below let you mark up platform usage.
              </Text>
              {Boolean(agencyData?.billing_plan?.subaccount_wallet) && (
                <HStack pl={6} mt={2} spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<SettingsIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      walletOnOpen();
                    }}
                  >
                    Configure wallet
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={multipliersOnOpen}
                  >
                    Multipliers
                  </Button>
                </HStack>
              )}
              {!isStripe && (
                <Text fontSize="xs" color="gray.400" pl={6} mt={1}>
                  Stripe billing method required to enable wallet.
                </Text>
              )}
            </Box>

            {/* Auto-deduct from agency wallet */}
            <Box>
              <HStack mb={1}>
                <Checkbox
                  {...register("deductAgencyWallet")}
                  isChecked={deductAgencyWallet}
                  onChange={(e) => {
                    saveBillingPlanPatch({
                      deduct_agency_wallet: e.target.checked,
                    });
                  }}
                >
                  <Text fontWeight="medium">
                    Auto-deduct from my agency wallet
                  </Text>
                </Checkbox>
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      icon={<IoInformationCircleOutline />}
                      variant="ghost"
                      size="xs"
                      aria-label="Auto-deduct info"
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverBody p={4} fontSize="sm">
                      Your subaccounts can use platform-managed keys
                      (OpenAI, ElevenLabs, etc.) instead of bringing
                      their own. With this enabled, their usage is
                      deducted from <strong>your agency wallet</strong>{" "}
                      at the{" "}
                      <a
                        rel="noreferrer"
                        href="https://docs.insighto.ai/getting-started/pricing"
                        target="_blank"
                        style={{ color: textColor, textDecoration: "underline" }}
                      >
                        pricing here
                      </a>
                      .
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </HStack>
              <Text fontSize="sm" color="gray.500" pl={6}>
                Don't make subaccounts bring their own provider keys —
                pay for their LLM / voice / etc. usage from your wallet.
              </Text>
            </Box>
          </Stack>
        </StepCard>

        {/* ─── Plans catalog ─── */}
        <Card variant="outline">
          <CardBody>
            <Flex justify="space-between" align="flex-start" mb={3}>
              <Box>
                <Heading size="sm" fontWeight="semibold">
                  Plans catalog
                </Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Plans your subaccounts can pick from. The first plan
                  is the default on signup and must be free.
                </Text>
              </Box>
              <Button
                colorScheme={buttonColorScheme}
                size="sm"
                isDisabled={
                  (!isCustom && !agencyData?.billing_plan?.subaccount_wallet) ||
                  (activeTabIndex === 3 &&
                    tableData.some(
                      (item) => item.billing_plan_group === "wallet"
                    ))
                }
                onClick={() => onOpen()}
              >
                Add plan
              </Button>
            </Flex>

            <Tabs
              isLazy
              index={activeTabIndex}
              onChange={(index) => setActiveTabIndex(index)}
            >
              <TabList>
                {tabs.map((tab, i) => (
                  <Tab
                    key={i}
                    color={textColor}
                    isDisabled={
                      agencyData?.billing_plan?.billing_method !== "custom" &&
                      !stripeKeyConfigured
                    }
                  >
                    {tab.value}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {tabs.map((tab, i) => (
                  <TabPanel key={i} px={0}>
                    {tab.children}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>

            {!stripeKeyConfigured && !isCustom && (
              <Alert status="info" borderRadius="md" mt={4}>
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Configure your Stripe credentials above to unlock the
                  plan catalog.
                </AlertDescription>
              </Alert>
            )}
          </CardBody>
        </Card>
      </Flex>

      {walletOpen && (
        <AddCustomBillingPlanWithWalletFields
          isOpen={walletOpen}
          onClose={walletOnClose}
          tableData={tableData[0]}
          fetchAgencyBillingPlanList={fetchAgencyBillingPlanList}
        />
      )}
      <ConfigureMultipliersModal
        isOpen={multipliersOpen}
        onClose={multipliersOnClose}
        fetchAgencyBillingPlanList={fetchAgencyBillingPlanList}
        tableData={tableData}
        onAgencyUpdated={() => {}}
      />
      {isOpen && (
        <AddCustomBillingPlan
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          tableData={tableData}
          fetchAgencyBillingPlanList={fetchAgencyBillingPlanList}
        />
      )}
    </>
  );
}
